import { useEffect, useRef, useState } from "react";
import { Star, Quote, MessageCircle, Loader2, ChevronDown, Share2, Facebook, Twitter, Link2, Check, Reply } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CORRIDORS } from "@/lib/constants";

const PAGE_SIZE = 6;

/**
 * Public reviews block — used on the landing page.
 * - Auto-fetches the latest 20 visible reviews and overall average.
 * - Users with a real (non-cancelled) transaction code can submit a review.
 */
export default function ReviewsSection() {
  const [items, setItems] = useState([]);
  const [average, setAverage] = useState(null);
  const [total, setTotal] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [filterRating, setFilterRating] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [showAll, setShowAll] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/reviews?limit=100");
      setItems(data?.items || []);
      setAverage(data?.average);
      setTotal(data?.total || 0);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { setShowAll(false); }, [filterRating, sortBy]);

  const filtered = filterRating === "all"
    ? items
    : items.filter((r) => Math.round(r.rating) === Number(filterRating));
  const ts = (r) => new Date(r.created_at || 0).getTime();
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "top") return (b.rating - a.rating) || (ts(b) - ts(a));
    if (sortBy === "oldest") return ts(a) - ts(b);
    return ts(b) - ts(a); // recent
  });
  const visible = showAll ? sorted : sorted.slice(0, PAGE_SIZE);

  return (
    <section className="bg-white border-t border-gray-100 py-16 sm:py-20" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">
              Avis clients
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">
              Ce qu&apos;ils en disent
            </h2>
            {average != null && total > 0 && (
              <div className="mt-3 inline-flex items-center gap-2" data-testid="reviews-avg">
                <Stars value={average} size={18} />
                <span className="text-sm font-semibold">{average.toFixed(1)}/5</span>
                <span className="text-sm text-gray-500">· {total} avis</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start">
            <div className="relative">
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e.target.value)}
                className="appearance-none rounded-full border border-gray-200 bg-white pl-4 pr-9 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30 cursor-pointer"
                data-testid="reviews-filter-rating"
                aria-label="Filtrer les avis par note"
              >
                <option value="all">Toutes les notes</option>
                <option value="5">5 étoiles</option>
                <option value="4">4 étoiles</option>
                <option value="3">3 étoiles</option>
                <option value="2">2 étoiles</option>
                <option value="1">1 étoile</option>
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none rounded-full border border-gray-200 bg-white pl-4 pr-9 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30 cursor-pointer"
                data-testid="reviews-sort"
                aria-label="Trier les avis"
              >
                <option value="recent">Plus récents</option>
                <option value="top">Mieux notés</option>
                <option value="oldest">Plus anciens</option>
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
            <button
              type="button"
              onClick={() => setOpenForm(true)}
              className="sl-btn-primary text-sm"
              data-testid="reviews-open-form"
            >
              <MessageCircle size={14} /> Laisser un avis
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="sl-card text-center text-sm text-gray-500" data-testid="reviews-empty">
            {filterRating === "all"
              ? "Aucun avis pour le moment. Soyez le premier à partager votre expérience !"
              : "Aucun avis avec cette note pour le moment."}
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-grid">
              {visible.map((r) => <ReviewCard key={r.id} r={r} />)}
            </div>
            {filtered.length > PAGE_SIZE && (
              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="sl-btn-outline text-sm"
                  data-testid="reviews-toggle-more"
                >
                  {showAll ? "Voir moins" : `Voir plus (${filtered.length - PAGE_SIZE})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {openForm && (
        <ReviewFormModal
          onClose={() => setOpenForm(false)}
          onCreated={() => { setOpenForm(false); refresh(); }}
        />
      )}
    </section>
  );
}

function formatReviewDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function ReviewCard({ r }) {
  const corridor = CORRIDORS[r.corridor];
  const dateStr = formatReviewDate(r.created_at);
  return (
    <article className="sl-card sl-card-hoverable relative" data-testid={`review-card-${r.id}`}>
      <div className="flex items-start justify-between">
        <Quote size={18} className="text-[var(--sl-primary)]/40 mb-2" />
        <ReviewShare r={r} />
      </div>
      <p className="text-sm text-gray-700 leading-relaxed line-clamp-6">{r.comment}</p>
      {r.reply && (
        <div className="mt-3 rounded-xl bg-[var(--sl-primary)]/5 border-l-2 border-[var(--sl-primary)]/40 pl-3 pr-3 py-2" data-testid={`review-reply-${r.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--sl-primary)] font-semibold flex items-center gap-1 mb-0.5">
            <Reply size={11} /> Réponse de Swiftlink
          </div>
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-4 whitespace-pre-line">{r.reply}</p>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <div className="font-semibold text-sm">{r.name || "Client Swiftlink"}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            {corridor && (
              <span className="text-[10px] text-gray-500 uppercase tracking-widest">{corridor.shortLabel || corridor.label}</span>
            )}
            {corridor && dateStr && <span className="text-[10px] text-gray-300">·</span>}
            {dateStr && (
              <span className="text-[10px] text-gray-400" data-testid={`review-date-${r.id}`}>{dateStr}</span>
            )}
          </div>
        </div>
        <Stars value={r.rating} size={14} />
      </div>
    </article>
  );
}

function ReviewShare({ r }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const url = typeof window !== "undefined" ? window.location.origin : "";
  const stars = "★".repeat(Math.round(r.rating || 0));
  const text = `${stars} « ${r.comment} » — Avis vérifié sur Swiftlink-Africa. Envoyez de l'argent en toute confiance entre le Togo, le Gabon, la France et le Rwanda.`;
  const shareText = `${text}\n${url}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Avis Swiftlink-Africa", text, url });
      } catch (_e) { /* user cancelled */ }
    } else {
      setOpen((v) => !v);
    }
  };

  const openWindow = (link) => {
    window.open(link, "_blank", "noopener,noreferrer,width=640,height=560");
    setOpen(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      toast.success("Avis copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      toast.error("Impossible de copier");
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={nativeShare}
        className="text-gray-400 hover:text-[var(--sl-primary)] hover:bg-[var(--sl-primary)]/10 p-1.5 rounded-full transition-colors"
        aria-label="Partager cet avis"
        data-testid={`review-share-${r.id}`}
      >
        <Share2 size={16} />
      </button>

      {open && (
        <div
          className="absolute right-0 top-9 z-20 w-52 rounded-xl border border-gray-200 bg-white shadow-xl p-1.5"
          data-testid={`review-share-menu-${r.id}`}
        >
          <button
            type="button"
            onClick={() => openWindow(`https://wa.me/?text=${encodeURIComponent(shareText)}`)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid={`review-share-whatsapp-${r.id}`}
          >
            <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp
          </button>
          <button
            type="button"
            onClick={() => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid={`review-share-facebook-${r.id}`}
          >
            <Facebook size={16} className="text-[#1877F2]" /> Facebook
          </button>
          <button
            type="button"
            onClick={() => openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid={`review-share-x-${r.id}`}
          >
            <Twitter size={16} className="text-gray-900" /> X (Twitter)
          </button>
          <button
            type="button"
            onClick={copyLink}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid={`review-share-copy-${r.id}`}
          >
            {copied ? <Check size={16} className="text-emerald-600" /> : <Link2 size={16} className="text-gray-500" />} Copier l&apos;avis
          </button>
        </div>
      )}
    </div>
  );
}

export function Stars({ value, size = 14 }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value} étoiles sur 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= Math.round(value) ? "text-amber-500" : "text-gray-300"}
          fill={i <= Math.round(value) ? "currentColor" : "none"}
        />
      ))}
    </div>
  );
}

function ReviewFormModal({ onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!code.trim()) { toast.error("Saisissez votre code de transaction (SL-XXXXXXXX)"); return; }
    if (!rating || rating < 1) { toast.error("Donnez une note"); return; }
    if (!comment.trim() || comment.trim().length < 5) { toast.error("Votre commentaire est trop court"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        transaction_code: code.trim().toUpperCase(),
        rating,
        comment: comment.trim(),
        name: name.trim() || undefined,
      });
      toast.success("Merci pour votre avis !");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="review-form-modal"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold">Votre avis compte</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1" aria-label="Fermer" data-testid="review-form-close">×</button>
        </div>

        <div className="rounded-xl bg-[var(--sl-bg-alt)] text-xs p-3 mb-4 text-gray-600">
          Pour garantir l&apos;authenticité, indiquez le code <span className="font-mono font-semibold">SL-XXXXXXXX</span> d&apos;un transfert que vous avez effectué.
        </div>

        <div className="space-y-3">
          <div>
            <label className="sl-label">Code de transaction</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SL-XXXXXXXX"
              className="sl-input font-mono"
              data-testid="review-code"
            />
          </div>

          <div>
            <label className="sl-label">Votre note</label>
            <div className="flex items-center gap-1" data-testid="review-rating">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRating(i)}
                  className="p-1"
                  aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
                  data-testid={`review-rating-${i}`}
                >
                  <Star size={26} className={i <= rating ? "text-amber-500" : "text-gray-300"} fill={i <= rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="sl-label">Votre prénom (optionnel)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. Jean"
              className="sl-input"
              data-testid="review-name"
            />
          </div>

          <div>
            <label className="sl-label">Votre commentaire</label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1500}
              placeholder="Partagez votre expérience…"
              className="sl-input"
              data-testid="review-comment"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="sl-btn-outline text-sm" data-testid="review-cancel">Annuler</button>
          <button type="button" onClick={submit} disabled={submitting} className="sl-btn-primary text-sm disabled:opacity-60" data-testid="review-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} Publier
          </button>
        </div>
      </div>
    </div>
  );
}
